import { LoadingOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Spin,
  TreeSelect,
} from "antd";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { GetPerfCycleTopLevelApi } from "../../api/perf-cycles/GetPerfCycleTopPlanApi";
import CascadeTopPlanApi from "../../api/plans/CascadeTopPlanApi";
import GetCascadeTreeApi from "../../api/units/GetCascadeTreeApi";
import { GetUnitPositionsApi2 } from "../../api/units/GetUnitPositionsApi2";
import GetDropdownOptionsByIdsApi from "../../api/attributes/GetDropdownOptionsByIdsApi";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../helpers/NotificationHelpers";
import PageLoader from "../sub/PageLoader";

const { Option } = Select;

const { TextArea } = Input;

function CascadePlanModal(props) {
  const [submit, setSubmit] = useState(false);
  const [unitsTree, setUnitsTree] = useState([]);
  const [unitPositions, setUnitPositions] = useState([]);
  const [topPlan, setTopPlan] = useState();
  const perfCycleState = useSelector((state) => state.perfCycleState.value);
  const topPlanState = useSelector((state) => state.topPlanState.value);
  const positionState = useSelector((state) => state.positionState.value);
  const [form] = Form.useForm();

  function handleCancel() {
    setSubmit(false);
    setUnitsTree([]);
    setUnitPositions([]);
    setTopPlan();
    form.resetFields();
    props.handleCancel();
  }

  useEffect(() => {
    if (props.visible) {
      getTopPlanDetails();
      getUnitsTree();
      form.setFields([
        {
          name: "title",
          value: props?.plan?.title,
        },
      ]);
    }
  }, [props.visible]);

  async function getTopPlanDetails() {
    const apiResponse = await GetPerfCycleTopLevelApi(perfCycleState.id);
    if (apiResponse.success) {
      const planData = apiResponse.data;

      const dropdownAttrIds = (planData?.planLevelAttributes || [])
        .filter((a) => a.valueType === "dropdown")
        .map((a) => a.id);

      if (dropdownAttrIds.length > 0) {
        const optionsRes = await GetDropdownOptionsByIdsApi(dropdownAttrIds);
        if (optionsRes.success && optionsRes.data) {
          planData.planLevelAttributes = planData.planLevelAttributes.map((attr) => {
            if (attr.valueType === "dropdown") {
              return { ...attr, dropdownOptions: optionsRes.data[attr.id] || [] };
            }
            return attr;
          });
        }
      }

      setTopPlan(planData);
    }
  }

  async function getUnitsTree() {
    const apiResponse = await GetCascadeTreeApi(props.plan?.unit?.unitId);
    if (apiResponse.message === "success") {
      setUnitsTree(apiResponse.units);
    }
  }

  async function onFinish(values) {
    setSubmit(true);
    let attributesValues = [];
    topPlan?.planLevelAttributes?.forEach((attribute) => {
      let value = values[attribute.id];
      // Convert array to comma-separated string for dropdown values
      if (Array.isArray(value)) {
        value = value.join(',');
      }
      attributesValues.push({
        attributeId: attribute.id,
        value: value,
      });
    });

    if (isTargetValid(values.target)) {
      const apiResponse = await CascadeTopPlanApi(
        props.plan?.planId,
        values.title,
        values.description || "",
        values.unit,
        values.target,
        values.targetDate || "",
        values.assignTo,
        positionState?.id,
        attributesValues
      );
      if (apiResponse.statusCode) {
        openNotificationError("bottomRight", apiResponse.message);
      } else if (apiResponse.message === "success") {
        openNotificationSuccess("bottomRight", `${topPlanState} cascaded`);
        props.handleOk(apiResponse.data);
      } else {
        openNotificationError(
          "bottomRight",
          "Couldn't add plan. Try again later."
        );
      }
      handleCancel();
    } else {
      form.setFields([
        {
          name: "target",
          errors: [`cannot exceed more than ${props.plan?.cascadeLimit}`],
        },
      ]);
      setSubmit(false);
    }
  }

  async function selectUnit(value) {
    const apiResponse = await GetUnitPositionsApi2(value);
    if (apiResponse.success) {
      setUnitPositions(apiResponse.data);
      form.setFields([
        {
          name: "assignTo",
          value: "",
        },
      ]);
    }
  }

  function isTargetValid(value) {
    if (!props.plan?.canExceed) {
      if (value > props.plan.cascadeLimit) {
        return false;
      }
    }
    return true;
  }

  const unitSelector = (
    <Form.Item name="unitScale" noStyle>
      <Select disabled style={{ width: 100 }}>
        <Option value={props.plan?.scale?.scaleUnitAbbreviation}>
          {props.plan?.scale?.scaleUnitAbbreviation}
        </Option>
      </Select>
    </Form.Item>
  );

  function renderDropdownAndBooleanAttributes() {
    let output = [];

    // Only render dropdown and boolean attributes
    const dropdownAndBooleanAttributes = topPlan?.planLevelAttributes?.filter((attribute) => {
      return attribute.valueType === "dropdown" || attribute.valueType === "boolean";
    }) || [];

    dropdownAndBooleanAttributes.forEach((attribute) => {
      output.push(
        <Col span={12} key={attribute.id}>
          <Form.Item label={attribute.name} name={attribute.id}>
            {attribute.valueType === "dropdown" ? (
              <Select
                mode="multiple"
                showSearch
                placeholder={`Select ${attribute.name}`}
                style={{ width: "100%" }}
                getPopupContainer={(trigger) => trigger.parentNode}
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {attribute.dropdownOptions?.map((option) => (
                  <Option key={option.id} value={option.id}>
                    {option.value}
                  </Option>
                ))}
              </Select>
            ) : attribute.valueType === "boolean" ? (
              <Select
                placeholder={`Select ${attribute.name}`}
                style={{ width: "100%" }}
                getPopupContainer={(trigger) => trigger.parentNode}
              >
                <Option value={true}>Yes</Option>
                <Option value={false}>No</Option>
              </Select>
            ) : null}
          </Form.Item>
        </Col>
      );
    });

    return output;
  }

  function renderOtherAttributes() {
    let output = [];

    // Filter out dropdown, boolean, and date attributes (all date attributes handled separately)
    const otherAttributes = topPlan?.planLevelAttributes?.filter((attribute) => {
      return !(attribute.valueType === "dropdown" || 
               attribute.valueType === "boolean" || 
               attribute.valueType === "date");
    }) || [];

    otherAttributes.forEach((attribute) => {
      output.push(
        <Col span={12} key={attribute.id}>
          <Form.Item label={attribute.name} name={attribute.id}>
            {attribute.valueType === "text" ? (
              <Input className="w-full" placeholder={attribute.name} />
            ) : attribute.valueType === "number" ? (
              <InputNumber
                min={0}
                style={{ width: "100%" }}
                placeholder={attribute.name}
              />
            ) : attribute.valueType === "double" ? (
              <InputNumber
                min={0}
                step={0.01}
                style={{ width: "100%" }}
                placeholder={attribute.name}
              />
            ) : attribute.valueType === "date" ? (
              <DatePicker className="w-full" placeholder={attribute.name} />
            ) : null}
          </Form.Item>
        </Col>
      );
    });

    return output;
  }



  return (
    <>
      <Modal
        title={`Cascade ${topPlanState}`}
        visible={props.visible}
        onCancel={() => handleCancel()}
        footer={null}
        destroyOnClose={true}
        width={900}
      >
        <div className="mb-2 text-base">
          {`Cascade from: ${props?.plan?.title}`}
        </div>

        <Form
          name="cascadePlan"
          onFinish={onFinish}
          autoComplete="off"
          layout="vertical"
          requiredMark={false}
          initialValues={{
            planGroup: props.plan?.planGroup,
            unitScale: props.plan?.scale?.scaleUnitAbbreviation,
            title: props?.plan?.title,
          }}
          form={form}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Plan Group*" name="planGroup">
                <Input disabled placeholder="Plan group" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={`Title*`}
                name="title"
                // initialValue={`${props?.plan?.title}`}
                rules={[
                  {
                    required: true,
                    message: `Please enter title!`,
                  },
                ]}
              >
                <Input placeholder={topPlanState} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Unit*"
                name="unit"
                rules={[
                  {
                    required: true,
                    message: "Please select a unit!",
                  },
                ]}
              >
                <TreeSelect
                  showSearch
                  treeLine={true}
                  dropdownStyle={{
                    maxHeight: 400,
                    overflow: "auto",
                  }}
                  treeData={unitsTree}
                  placeholder="Select unit"
                  treeDefaultExpandAll
                  treeNodeFilterProp={"title"}
                  showIcon={false}
                  filterTreeNode
                  onChange={(value) => selectUnit(value)}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Assign to*"
                name="assignTo"
                rules={[
                  {
                    required: true,
                    message: "Please select a position!",
                  },
                ]}
              >
                <Select
                  showSearch
                  placeholder="Select position"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  filterSort={(optionA, optionB) =>
                    optionA.children
                      .toLowerCase()
                      .localeCompare(optionB.children.toLowerCase())
                  }
                >
                  {unitPositions.map((position) => {
                    return (
                      <Option key={position.id} value={position.id}>
                        {(position.user?.firstName
                          ? position.user?.firstName + ", "
                          : "") + position.designation?.name}
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Target*"
                name="target"
                rules={[
                  {
                    required: true,
                    message: "Please enter target!",
                  },
                ]}
              >
                <InputNumber
                  addonAfter={
                    props.plan?.scale?.scaleName === "Number"
                      ? props.plan?.scale?.scaleName === "Percentage" && "%"
                      : unitSelector
                  }
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>

            {topPlan?.description && (
              <Col span={24}>
                <Form.Item label="Description" name="description">
                  <TextArea
                    showCount
                    maxLength={600}
                    placeholder="Description"
                  />
                </Form.Item>
              </Col>
            )}

            {renderDropdownAndBooleanAttributes()}

            {topPlan?.targetDate && (
              <Col span={12}>
                <Form.Item label="Target date*" name="targetDate">
                  <DatePicker className="w-full" placeholder="Target date" />
                </Form.Item>
              </Col>
            )}

            {renderOtherAttributes()}

            {!topPlan && <PageLoader />}

            <Col span={24}>
              <Form.Item style={{ marginBottom: 0 }}>
                <div className=" flex justify-end">
                  <Button className="mr-3 w-28" onClick={() => handleCancel()}>
                    Cancel
                  </Button>
                  <Button
                    className="btn w-40"
                    type="primary"
                    htmlType={submit && "submit"}
                    disabled={submit}
                  >
                    {submit ? (
                      <Spin
                        size="small"
                        indicator={
                          <LoadingOutlined style={{ color: "#fff" }} spin />
                        }
                      />
                    ) : (
                      `Cascade`
                    )}
                  </Button>
                </div>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}

export default CascadePlanModal;
